import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import appConfig from '../../../config/appConfig';

const CURRENCY = appConfig.stripeCurrency || '$';

function formatNumber(value) {
  if (value === '' || value === null || value === undefined) return '';
  return new Intl.NumberFormat(window.navigator.language).format(value);
}

function TierRow({
  tier,
  index,
  isLast,
  isSecondToLast,
  totalTiers,
  onChange,
  onDelete,
  onUpToChange,
  readOnly = false,
  disabled = false,
}) {
  const isInfinite = tier.up_to === 'inf';
  const isOnlyLastTwo = totalTiers <= 2;

  const handleUnitAmount = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    onChange(index, 'unit_amount', value === '' ? 0 : parseFloat(value) || 0);
  };

  const handleFlatAmount = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    onChange(index, 'flat_amount', value === '' ? 0 : parseFloat(value) || 0);
  };

  const handleUpTo = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    onUpToChange(index, value === '' ? '' : parseInt(value, 10) || 0);
  };

  const preventInvalidKeys = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };

  const hasOverlap = !isInfinite && tier.up_to < tier.starting_from;

  return (
    <Stack direction="row" sx={{ flexGrow: 1, gap: 1 }}>
      <TextField
        value={formatNumber(tier.starting_from)}
        size="small"
        label="From"
        disabled
        fullWidth
        sx={{
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'text.secondary',
            color: 'text.secondary',
          },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="text.disabled">
                  #
                </Typography>
              </InputAdornment>
            ),
          },
        }}
      />
      {isInfinite ? (
        <TextField
          value="Unlimited"
          size="small"
          label="Up to"
          disabled
          fullWidth
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'text.secondary',
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <AllInclusiveIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        />
      ) : (
        <TextField
          value={tier.up_to === '' ? '' : formatNumber(tier.up_to)}
          size="small"
          label="Up to"
          fullWidth
          error={hasOverlap}
          helperText={hasOverlap ? 'Range overlaps' : ' '}
          onChange={handleUpTo}
          disabled={disabled}
          placeholder="e.g. 1000"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.disabled">
                    #
                  </Typography>
                </InputAdornment>
              ),
              readOnly: readOnly,
            },
          }}
        />
      )}
      <TextField
        type="number"
        value={tier.unit_amount}
        size="small"
        label="Per Unit"
        fullWidth
        onChange={handleUnitAmount}
        onKeyDown={preventInvalidKeys}
        disabled={disabled}
        placeholder="0.00"
        helperText=" "
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="text.secondary">
                  {CURRENCY}
                </Typography>
              </InputAdornment>
            ),
            readOnly: readOnly,
          },
        }}
      />
      <TextField
        type="number"
        value={tier.flat_amount}
        size="small"
        label="Flat fee"
        fullWidth
        onChange={handleFlatAmount}
        onKeyDown={preventInvalidKeys}
        disabled={disabled}
        placeholder="0.00"
        helperText=" "
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="text.secondary">
                  {CURRENCY}
                </Typography>
              </InputAdornment>
            ),
            readOnly: readOnly,
          },
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
        {!readOnly && isSecondToLast && !isOnlyLastTwo ? (
          <Tooltip title="Remove tier" placement="top">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(index)}
                disabled={disabled}
                sx={{
                  border: '1px solid',
                  borderColor: 'error.light',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'error.50', borderColor: 'error.main' },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ width: 34 }} />
        )}
      </Box>
    </Stack>
  );
}

export default function TieredPriceInput({ formData, onChange, disabled = false, readOnly = false }) {
  const [tiers, setTiers] = useState(formData.tiers);

  const notifyChange = useCallback(
    (updatedTiers) => {
      onChange({ ...formData, tiers: updatedTiers });
    },
    [formData, onChange],
  );

  const handleFieldChange = (index, field, value) => {
    setTiers((prev) => {
      const updated = prev.map((t, i) => (i === index ? { ...t, [field]: value } : t));
      notifyChange(updated);
      return updated;
    });
  };

  const handleUpToChange = (index, value) => {
    setTiers((prev) => {
      const updated = prev.map((tier, i) => {
        if (i === index) return { ...tier, up_to: value };
        if (i > index) return { ...tier, starting_from: typeof value === 'number' ? value + 1 : value, up_to: 'inf' };
        return tier;
      });
      notifyChange(updated);
      return updated;
    });
  };

  const addTier = () => {
    setTiers((prev) => {
      const lastIdx = prev.length - 1;
      const last = prev[lastIdx];
      const secondToLast = prev[lastIdx - 1];

      const newStarting =
        secondToLast && typeof secondToLast.up_to === 'number' ? secondToLast.up_to + 1 : last.starting_from;

      const newUpTo = newStarting + 99;

      const updated = [
        ...prev.slice(0, lastIdx).map((t, i) => (i === lastIdx - 1 ? { ...t, up_to: newStarting - 1 } : t)),
        { ...last, starting_from: newStarting, up_to: newUpTo },
        { starting_from: newUpTo + 1, up_to: 'inf', unit_amount: 0, flat_amount: 0 },
      ];
      notifyChange(updated);
      return updated;
    });
  };

  const deleteTier = (index) => {
    setTiers((prev) => {
      if (prev.length <= 2) return prev;
      const updated = prev.filter((_, i) => i !== index);
      if (updated[index]) {
        updated[index] = { ...updated[index], starting_from: prev[index].starting_from };
      }
      notifyChange(updated);
      return updated;
    });
  };

  useEffect(() => {
    setTiers(formData.tiers);
  }, [formData.tiers]);

  const totalCost = (tier) => {
    const perUnit = parseFloat(tier.unit_amount) || 0;
    const flat = parseFloat(tier.flat_amount) || 0;
    const units = typeof tier.up_to === 'number' ? tier.up_to - tier.starting_from + 1 : null;
    if (units === null) return null;
    return (perUnit * units + flat).toFixed(2);
  };

  return (
    <Box>
      {/* Header summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${tiers.length} tier${tiers.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: 12 }}
          />
          <Typography variant="caption" color="text.secondary">
            Prices apply based on total usage within each range
          </Typography>
        </Box>
      </Box>

      {/* Tier rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {tiers.map((tier, index) => (
          <TierRow
            key={index}
            tier={tier}
            index={index}
            isLast={index === tiers.length - 1}
            isSecondToLast={index === tiers.length - 2}
            totalTiers={tiers.length}
            onChange={handleFieldChange}
            onDelete={deleteTier}
            onUpToChange={handleUpToChange}
            disabled={disabled}
            readOnly={readOnly}
          />
        ))}
      </Box>

      {/* Add tier */}
      {!disabled && (
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addTier} sx={{ mt: 2 }}>
          Add tier
        </Button>
      )}
    </Box>
  );
}
